/**
 * PlayView — the gameboard.
 *
 * One PlayView instance per ladder; App keys on (worldId, ladderIdx) so
 * navigating between ladders remounts this component with fresh state.
 *
 * Owns:
 *   - graph + validAtSlot computation (effect)
 *   - per-slot state (typed buffer, hint level)
 *   - solve detection + persistence
 *   - hidden capture <input> + keyboard plumbing
 *   - pager sheet open/closed
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { AdjacencyList, Locale, SlotState, World } from './types';
import { loadGraph } from './data';
import { computeValidAtSlot, letterDiff } from './bfs';
import { fetchDefinition } from './dictionary';
import {
  isSolved as readSolved,
  getSavedSolution,
  getSavedProgress,
  hasSavedProgress,
  markSolved,
  saveProgress,
  clearProgress,
  clearSolved,
  getSolvedSet,
} from './storage';
import { useConfirm } from './ConfirmDialog';

interface Props {
  readonly locale: Locale;
  readonly world: World;
  readonly ladderIdx: number;
  readonly onBackToWorlds: () => void;
  readonly onOpenSettings: () => void;
  readonly onOpenLookup: (word: string) => void;
  readonly onPickLadder: (idx: number) => void;
}

const TUT_STR = '↑ Tap any word for its full meaning and examples';

export function PlayView(props: Props) {
  const { locale, world, ladderIdx, onBackToWorlds, onOpenSettings, onOpenLookup, onPickLadder } = props;
  const confirmDialog = useConfirm();
  const ladder = world.ladders[ladderIdx]!;
  const N = ladder.optimalSwaps;
  const length = ladder.start.length;

  // ──────────────── Graph + valid-at-slot (async on mount) ────────────────
  const [graph, setGraph] = useState<AdjacencyList | null>(null);
  const [validAtSlot, setValidAtSlot] = useState<readonly Set<string>[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const g = await loadGraph(locale, length);
      if (cancelled) return;
      setGraph(g);
      setValidAtSlot(computeValidAtSlot(ladder.start, ladder.target, N, g));
    })();
    return () => { cancelled = true; };
  }, [locale, length, ladder.start, ladder.target, N]);

  // ──────────────── Ladder state ────────────────
  const initial = useMemo(() => initState(locale, world, ladderIdx, N), [locale, world, ladderIdx, N]);
  const [slots, setSlots] = useState<SlotState[]>(initial.slots);
  const [activeIdx, setActiveIdx] = useState<number>(initial.activeIdx);
  const [solved, setSolved] = useState<boolean>(initial.solved);
  const [hintsUsed, setHintsUsed] = useState<number>(initial.hintsUsed);
  // For solved+restored ladders, the stored hint count may be null (legacy).
  // We surface the legacy null separately so the note can fall back.
  const [storedHintsUsed, setStoredHintsUsed] = useState<number | null>(initial.storedHintsUsed);

  // ──────────────── Pager sheet ────────────────
  const [pagerOpen, setPagerOpen] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);  // bump to re-pull picker state

  // ──────────────── Capture input ────────────────
  const captureRef = useRef<HTMLInputElement>(null);
  const ladderRef = useRef<HTMLOListElement>(null);

  // ──────────────── Endpoint + intermediate definitions ────────────────
  const [defs, setDefs] = useState<Record<number, string>>({});  // slotIdx → "pos · gloss"

  useEffect(() => {
    let cancelled = false;
    const showFor = async (slotIdx: number, word: string) => {
      const def = await fetchDefinition(locale, word);
      if (cancelled) return;
      if (!def) { setDefs((d) => ({ ...d, [slotIdx]: '' })); return; }
      const pos = def.partOfSpeech ? `${def.partOfSpeech.toLowerCase()} · ` : '';
      setDefs((d) => ({ ...d, [slotIdx]: `${pos}${def.senses[0]?.gloss ?? ''}` }));
    };
    showFor(0, ladder.start);
    showFor(N, ladder.target);
    // Restored intermediates also need their cards populated
    for (let i = 0; i < slots.length; i++) {
      const w = slots[i]!.word;
      if (w) showFor(i + 1, w);
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);  // run once per mount; the keying on (world, ladderIdx) handles re-runs

  // ──────────────── Input handlers ────────────────
  const focusCapture = () => {
    if (solved) { captureRef.current?.blur(); return; }
    if (captureRef.current) {
      captureRef.current.value = '';
      captureRef.current.focus({ preventScroll: true });
    }
  };
  const blurCapture = () => captureRef.current?.blur();

  const feedChar = (ch: string) => {
    if (solved) return;
    setSlots((prev) => {
      const next = prev.slice();
      const slot = next[activeIdx];
      if (!slot) return prev;
      if (slot.typed.length >= length) return prev;
      const newSlot = { ...slot, typed: slot.typed + ch.toUpperCase() };
      next[activeIdx] = newSlot;
      // If now full, schedule submission
      if (newSlot.typed.length === length) {
        queueMicrotask(() => attemptSubmitForTyped(newSlot.typed));
      }
      return next;
    });
  };

  const backspaceChar = () => {
    if (solved) return;
    setSlots((prev) => {
      const next = prev.slice();
      const slot = next[activeIdx];
      if (!slot || slot.typed.length === 0) return prev;
      next[activeIdx] = { ...slot, typed: slot.typed.slice(0, -1) };
      return next;
    });
  };

  const attemptSubmitForTyped = (typed: string) => {
    const slotPos = activeIdx + 1;  // 0 = start, last = target
    if (validAtSlot.length === 0) return;
    const valid = validAtSlot[slotPos]?.has(typed) && oneSwapFromPrev(typed, slotPos);
    if (!valid) {
      flashInvalid();
      setSlots((prev) => {
        const next = prev.slice();
        next[activeIdx] = { ...next[activeIdx]!, typed: '' };
        return next;
      });
      focusCapture();
      return;
    }

    // Commit the typed word as the slot's value
    setSlots((prev) => {
      const next = prev.slice();
      next[activeIdx] = { ...next[activeIdx]!, word: typed, typed: '' };
      return next;
    });
    const newActiveIdx = activeIdx + 1;
    setActiveIdx(newActiveIdx);

    // Show definition for the just-filled slot
    (async () => {
      const def = await fetchDefinition(locale, typed);
      if (!def) { setDefs((d) => ({ ...d, [slotPos]: '' })); return; }
      const pos = def.partOfSpeech ? `${def.partOfSpeech.toLowerCase()} · ` : '';
      setDefs((d) => ({ ...d, [slotPos]: `${pos}${def.senses[0]?.gloss ?? ''}` }));
    })();

    const projected: SlotState[] = slots.map((s, i) =>
      i === activeIdx ? { ...s, word: typed, typed: '' } : s
    );
    saveProgress(locale, world.worldId, ladderIdx, projected.map((s) => s.word));

    // Final-slot check: is the just-filled intermediate one swap from target?
    if (newActiveIdx === slots.length) {
      const lastWord = typed;
      if (letterDiff(lastWord, ladder.target) === 1) {
        // Solved. CRITICAL: do NOT focusCapture() afterwards — the React
        // state flip for `solved` hasn't taken effect yet so focusCapture's
        // closure still sees solved=false and would re-pop the keyboard.
        // Just blur and bail.
        const fullPath = [
          ladder.start,
          ...projected.map((s) => s.word).filter((x): x is string => x !== null),
          ladder.target,
        ];
        markSolved(locale, world.worldId, ladderIdx, fullPath, hintsUsed);
        clearProgress(locale, world.worldId, ladderIdx);
        setSolved(true);
        setStoredHintsUsed(hintsUsed);
        setRefreshTick((n) => n + 1);
        blurCapture();
        return;  // ← skip the trailing focusCapture()
      }
      // Dead end: last fill isn't one-swap from target. Roll back to
      // the active slot so the player can try a different word.
      flashInvalid();
      setSlots((prev) => {
        const next = prev.slice();
        next[activeIdx] = { ...next[activeIdx]!, word: null };
        return next;
      });
      setActiveIdx(activeIdx);
      saveProgress(locale, world.worldId, ladderIdx, projected.map((s, i) => (i === activeIdx ? null : s.word)));
      focusCapture();
      return;
    }

    // Mid-ladder: more slots to fill, keep the keyboard up
    focusCapture();
  };

  const oneSwapFromPrev = (word: string, slotPos: number): boolean => {
    const prev = slotPos === 1 ? ladder.start : slots[slotPos - 2]?.word;
    if (!prev) return false;
    return letterDiff(word, prev) === 1;
  };

  const flashInvalid = () => {
    const lis = ladderRef.current?.querySelectorAll('li');
    if (!lis) return;
    const li = lis[activeIdx + 1];
    if (!li) return;
    li.classList.add('invalid');
    setTimeout(() => li.classList.remove('invalid'), 350);
  };

  // ──────────────── Hint ────────────────
  const handleHint = async () => {
    if (solved) return;
    const slot = slots[activeIdx];
    if (!slot) return;
    if (slot.hintLevel >= 4) return;

    const newLevel = slot.hintLevel + 1;
    setHintsUsed((h) => h + 1);

    if (newLevel === 1) {
      const canonical = ladder.canonicalPath[activeIdx + 1];
      if (canonical) {
        const def = await fetchDefinition(locale, canonical);
        let hintDef = '(definition unavailable — try the next hint)';
        if (def) {
          const pos = def.partOfSpeech ? `${def.partOfSpeech.toLowerCase()} · ` : '';
          hintDef = `${pos}${def.senses[0]?.gloss ?? ''}`;
        }
        setSlots((prev) => {
          const next = prev.slice();
          next[activeIdx] = { ...next[activeIdx]!, hintLevel: 1, hintDefinition: hintDef };
          return next;
        });
      }
      return;
    }

    if (newLevel === 4) {
      // Auto-fill the canonical word
      const word = ladder.canonicalPath[activeIdx + 1];
      if (word) {
        setSlots((prev) => {
          const next = prev.slice();
          next[activeIdx] = { ...next[activeIdx]!, hintLevel: 4, typed: word };
          return next;
        });
        queueMicrotask(() => attemptSubmitForTyped(word));
      }
      return;
    }

    setSlots((prev) => {
      const next = prev.slice();
      next[activeIdx] = { ...next[activeIdx]!, hintLevel: newLevel };
      return next;
    });
  };

  // ──────────────── Reset (on solved) / Restart (on unsolved) ────────────────
  const handleBottomLeft = async () => {
    if (solved) {
      const ok = await confirmDialog({
        title: 'Reset this ladder?',
        message: 'Clears your saved solve so you can play this ladder again from scratch. Your other progress is untouched.',
        confirmLabel: 'Reset and replay',
        cancelLabel: 'Keep my solve',
        danger: true,
      });
      if (!ok) return;
      clearSolved(locale, world.worldId, ladderIdx);
      clearProgress(locale, world.worldId, ladderIdx);
      // Reset in-component state to fresh
      const fresh = freshSlots(N);
      setSlots(fresh);
      setActiveIdx(0);
      setSolved(false);
      setHintsUsed(0);
      setStoredHintsUsed(null);
      setDefs({});
      setRefreshTick((n) => n + 1);
      // Re-fetch endpoint defs
      (async () => {
        const a = await fetchDefinition(locale, ladder.start);
        if (a) {
          const pos = a.partOfSpeech ? `${a.partOfSpeech.toLowerCase()} · ` : '';
          setDefs((d) => ({ ...d, 0: `${pos}${a.senses[0]?.gloss ?? ''}` }));
        }
        const b = await fetchDefinition(locale, ladder.target);
        if (b) {
          const pos = b.partOfSpeech ? `${b.partOfSpeech.toLowerCase()} · ` : '';
          setDefs((d) => ({ ...d, [N]: `${pos}${b.senses[0]?.gloss ?? ''}` }));
        }
      })();
    } else {
      // Restart in-session attempt; clear partial-progress storage too.
      // No confirm — this is a non-destructive in-session clear and the
      // partial-progress entry being wiped is what the player just typed.
      setSlots(freshSlots(N));
      setActiveIdx(0);
      clearProgress(locale, world.worldId, ladderIdx);
      setRefreshTick((n) => n + 1);
      blurCapture();
    }
  };

  const handleNextLadderCta = () => {
    const isLast = ladderIdx >= world.ladders.length - 1;
    if (isLast) onBackToWorlds();
    else onPickLadder(ladderIdx + 1);
  };

  // ──────────────── beforeinput + keydown wiring ────────────────
  useEffect(() => {
    const onBefore = (e: InputEvent) => {
      if (solved) return;
      if ((e.inputType === 'insertText' || e.inputType === 'insertCompositionText') && e.data) {
        e.preventDefault();
        for (const ch of e.data) {
          if (/^[a-zA-ZÀ-ÿß]$/.test(ch)) feedChar(ch);
        }
        return;
      }
      if (e.inputType === 'deleteContentBackward') {
        e.preventDefault();
        backspaceChar();
        return;
      }
      if (e.inputType === 'insertLineBreak') {
        e.preventDefault();
        const slot = slots[activeIdx];
        if (slot && slot.typed.length === length) attemptSubmitForTyped(slot.typed);
      }
    };
    const onInput = () => {
      if (captureRef.current && captureRef.current.value.length > 0) {
        captureRef.current.value = '';
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (solved) return;
      if (e.key === 'Enter') {
        e.preventDefault();
        const slot = slots[activeIdx];
        if (slot && slot.typed.length === length) attemptSubmitForTyped(slot.typed);
      }
    };
    const node = captureRef.current;
    node?.addEventListener('beforeinput', onBefore as EventListener);
    node?.addEventListener('input', onInput);
    document.addEventListener('keydown', onKey);
    return () => {
      node?.removeEventListener('beforeinput', onBefore as EventListener);
      node?.removeEventListener('input', onInput);
      document.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIdx, slots, solved, validAtSlot]);

  // Tap-on-ladder routing: lookup vs focus-capture
  const onLadderClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('button')) return;
    const wordEl = target.closest('[data-word]') as HTMLElement | null;
    if (wordEl) {
      onOpenLookup(wordEl.dataset.word!);
      return;
    }
    const activeLi = target.closest('li.active');
    if (activeLi) focusCapture();
  };

  // ──────────────── Render ────────────────
  const isLast = ladderIdx >= world.ladders.length - 1;
  const ctaLabel = isLast ? '← Worlds' : 'Next ladder →';
  const noteHints = storedHintsUsed !== null && solved ? storedHintsUsed : hintsUsed;

  return (
    <section id="play" className="view">
      <header className="topbar">
        <button className="navBtn" onClick={onBackToWorlds}>← Worlds</button>
        <div className="worldName">{world.themeName}</div>
        <button className="iconBtn ghost" aria-label="Settings" onClick={onOpenSettings}>⋯</button>
      </header>
      <div className="ladderNav">
        <button
          className="iconBtn"
          aria-label="Previous ladder"
          onClick={() => onPickLadder(ladderIdx - 1)}
          disabled={ladderIdx === 0}
        >◂</button>
        <button className="pager" onClick={() => setPagerOpen((p) => !p)}>
          {ladderIdx + 1} / {world.ladders.length} ▾
        </button>
        <button
          className="iconBtn"
          aria-label="Next ladder"
          onClick={() => onPickLadder(ladderIdx + 1)}
          disabled={ladderIdx === world.ladders.length - 1}
        >▸</button>
      </div>
      <PagerSheet
        open={pagerOpen}
        world={world}
        locale={locale}
        currentIdx={ladderIdx}
        refreshTick={refreshTick}
        onPick={(i) => { setPagerOpen(false); onPickLadder(i); }}
      />
      <div className="board">
        <ol className="ladder" ref={ladderRef} onClick={onLadderClick}>
          {/* Start word */}
          <WordRow slotIdx={0} word={ladder.start} endpoint def={defs[0] ?? ''} />

          {/* Intermediates */}
          {slots.map((slot, i) => {
            const slotIdx = i + 1;
            const isActiveRow = !solved && slotIdx === activeIdx + 1;
            if (slot.word) {
              const prev = i === 0 ? ladder.start : slots[i - 1]!.word!;
              return (
                <WordRow
                  key={slotIdx}
                  slotIdx={slotIdx}
                  word={slot.word}
                  prev={prev}
                  def={defs[slotIdx] ?? ''}
                />
              );
            }
            return (
              <ActiveSlotRow
                key={slotIdx}
                slotIdx={slotIdx}
                slot={slot}
                isActive={isActiveRow}
                length={length}
                canonicalNext={ladder.canonicalPath[slotIdx] ?? ''}
                prevWord={i === 0 ? ladder.start : (slots[i - 1]!.word ?? '')}
              />
            );
          })}

          {/* Target word */}
          <WordRow slotIdx={N} word={ladder.target} endpoint def={defs[N] ?? ''} />

          {/* Solved card */}
          {solved && (
            <li className="solved-card">
              <div className="solved-mark">✓ Solved</div>
              <div className="solved-note">{solvedNoteText(noteHints, N, storedHintsUsed === null)}</div>
              <div className="solved-tap-hint">{TUT_STR}</div>
            </li>
          )}
        </ol>
      </div>
      <footer className="bottombar">
        {!solved && <button onClick={handleHint}>Hint</button>}
        <button
          className={solved ? 'danger' : 'ghost'}
          onClick={handleBottomLeft}
          title={solved ? 'Clear this solve and play again' : 'Clear current attempt'}
        >
          {solved ? 'Reset' : 'Restart'}
        </button>
        {solved && (
          <button id="nextLadderCta" onClick={handleNextLadderCta}>{ctaLabel}</button>
        )}
      </footer>

      {/* Hidden capture <input> — drives the soft keyboard. Placed inside
          the play section so it gets removed when navigating away. */}
      <input
        ref={captureRef}
        id="capture"
        type="text"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="characters"
        spellCheck={false}
        inputMode="text"
        aria-label="Type the next word"
      />
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Helpers + sub-components

function freshSlots(N: number): SlotState[] {
  return Array.from({ length: N - 1 }, () => ({
    word: null, hintLevel: 0, typed: '', hintDefinition: null,
  }));
}

function initState(locale: Locale, world: World, ladderIdx: number, N: number): {
  slots: SlotState[]; activeIdx: number; solved: boolean; hintsUsed: number; storedHintsUsed: number | null;
} {
  const slots = freshSlots(N);
  let activeIdx = 0;
  let hintsUsed = 0;
  let storedHintsUsed: number | null = null;
  const solved = readSolved(locale, world.worldId, ladderIdx);

  if (solved) {
    const saved = getSavedSolution(locale, world.worldId, ladderIdx);
    if (saved && saved.intermediates.length === slots.length) {
      saved.intermediates.forEach((w, i) => { slots[i]!.word = w; });
      activeIdx = slots.length;
      storedHintsUsed = saved.hintsUsed;
      hintsUsed = saved.hintsUsed ?? 0;
    }
  } else {
    const partial = getSavedProgress(locale, world.worldId, ladderIdx);
    if (partial && partial.length === slots.length) {
      partial.forEach((w, i) => { if (w) slots[i]!.word = w; });
      const firstEmpty = slots.findIndex((s) => s.word === null);
      activeIdx = firstEmpty === -1 ? slots.length : firstEmpty;
    }
  }
  return { slots, activeIdx, solved, hintsUsed, storedHintsUsed };
}

function solvedNoteText(hintsUsed: number, swaps: number, isLegacy: boolean): string {
  if (isLegacy) return `${swaps} swaps`;
  if (hintsUsed === 0) return `Cleanly solved · ${swaps} swaps`;
  if (hintsUsed === 1) return `Solved with one hint · ${swaps} swaps`;
  return `Solved with ${hintsUsed} hints · ${swaps} swaps`;
}

function WordRow(props: {
  slotIdx: number;
  word: string;
  endpoint?: boolean;
  prev?: string;
  def: string;
}) {
  const { word, endpoint, prev, def } = props;
  const chars = [...word];
  const prevChars = prev ? [...prev] : null;
  return (
    <li className={'filled' + (endpoint ? ' endpoint' : '')}>
      <div
        className={'word' + (endpoint ? ' endpoint' : '')}
        data-word={word}
        role="button"
        title="Tap to look up"
      >
        {chars.map((c, i) => {
          const changed = prevChars && prevChars[i] !== c;
          return (
            <span key={i} className={'ch' + (changed ? ' changed' : '')}>{c}</span>
          );
        })}
      </div>
      <div className="definition">{def}</div>
    </li>
  );
}

function ActiveSlotRow(props: {
  slotIdx: number;
  slot: SlotState;
  isActive: boolean;
  length: number;
  canonicalNext: string;
  prevWord: string;
}) {
  const { slot, isActive, length, canonicalNext, prevWord } = props;

  // For hint level >= 2, show a mask: known letters from canonical word at
  // unchanged positions, and either '_' or the actual letter at the
  // changing position based on hint level.
  let hintMask: string[] | null = null;
  if (slot.hintLevel >= 2 && canonicalNext && prevWord) {
    const tgt = [...canonicalNext];
    const prev = [...prevWord];
    hintMask = tgt.map((ch, i) => (ch !== prev[i] ? (slot.hintLevel >= 3 ? ch : '_') : ch));
  }
  const typedPadded = slot.typed.padEnd(length, ' ').slice(0, length);

  return (
    <li className={isActive ? 'active' : ''}>
      <div className="input">
        {Array.from({ length }, (_, i) => {
          const typedCh = typedPadded[i]?.trim() ?? '';
          let cls = 'slot';
          let text = '·';
          if (typedCh) {
            text = typedCh;
            cls += ' typed';
          } else if (hintMask && hintMask[i] && hintMask[i] !== '_') {
            text = hintMask[i]!;
            cls += ' hinted';
          }
          if (isActive && i === slot.typed.length && !hintMask) cls += ' caret';
          return <span key={i} className={cls}>{text}</span>;
        })}
      </div>
      <div className={'definition' + (slot.hintDefinition ? ' visible' : '')}>
        {slot.hintDefinition ?? ''}
      </div>
    </li>
  );
}

function PagerSheet(props: {
  open: boolean;
  world: World;
  locale: Locale;
  currentIdx: number;
  refreshTick: number;
  onPick: (idx: number) => void;
}) {
  const { open, world, locale, currentIdx, onPick } = props;
  if (!open) return null;
  const solvedSet = getSolvedSet();
  return (
    <div className="pagerSheet">
      {world.ladders.map((l, i) => {
        const isActive = i === currentIdx;
        const isSolvedHere = solvedSet.has(`${locale}|${world.worldId}|${i}`);
        const isInProgress = !isSolvedHere && hasSavedProgress(locale, world.worldId, i);
        const cls =
          (isActive ? 'active' : '') +
          (isSolvedHere ? ' solved' : '') +
          (isInProgress ? ' inProgress' : '');
        return (
          <button
            key={i}
            className={cls.trim()}
            title={`${l.start} → ${l.target}`}
            onClick={() => onPick(i)}
          >
            {i + 1}
          </button>
        );
      })}
    </div>
  );
}
